// @flow
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, size } from 'lodash';
import classNames from 'classnames';
import { Badge, FormattedText, Icon, Spinner } from '@elements';
import { createNsTranslator } from '@helpers/helpers';
import { balanceStatus, countsTowardBalance, latestDate, listState, recentMovements, shortDate } from './financialOverview';
import { paymentMethodText } from './PatientFinancialPayments';
import './PatientFinancialOverview.scss';

type Props = {
  patient: ?Object,
  services: Object,
  payments: Object,
  receipts: Object,
  isBalanceLoading: boolean,
  // This patient's own balance request (isBalanceLoading is shared: saves and other patients set it too)
  balanceRequest?: ?string,
  paymentMethods: Array<Object>,
  serviceLoadingIds?: Object,
  paymentLoadingIds?: Object,
  onNewService: Function,
  onNewPayment: Function,
  onNewReceipt: Function,
  onOpenList: (list: 'services' | 'payments' | 'receipts') => void,
  onOpenService: (service: Object) => void,
  onOpenPayment: (payment: Object) => void,
  onRetry: Function,
};

const RECENT_LIMIT = 5;

// One icon and color per kind, the same in the quick actions, the list rows and the movements ($ is what the app
// shows for services elsewhere: Serviços contratados, the services table)
const KIND_ICON = { service: 'services', payment: 'plan', receipt: 'doc' };

const Kind = ({ kind, size: iconSize = 20 }: { kind: string, size?: number }) => (
  <span className={`financialKind financialKind-${kind}`} aria-hidden="true">
    <Icon iconName={KIND_ICON[kind]} size={iconSize} />
  </span>
);

const Line = ({ width }: { width: string }) => <span className={`skeletonLine ${width}`} />;

// The phone's Financeiro tab: the balance in plain words, the three "new" actions one tap away,
// the lists as rows with what's in them, and the latest services and payments.
const PatientFinancialOverview = ({
  patient, services, payments, receipts, isBalanceLoading, balanceRequest, paymentMethods,
  serviceLoadingIds, paymentLoadingIds,
  onNewService, onNewPayment, onNewReceipt, onOpenList, onOpenService, onOpenPayment, onRetry,
}: Props) => {
  const { t } = useTranslation();
  const _ = createNsTranslator('financial', t);
  const [showAll, setShowAll] = useState(false);

  // Balance
  const balance = get(patient, 'financialBalance');
  const hasBalance = balance !== undefined && balance !== null && balance !== '';
  const status = hasBalance ? balanceStatus(balance) : null;
  const balancePending = isBalanceLoading || balanceRequest === 'pending';
  const balanceFailed = balanceRequest === 'failed';
  const sumOfServices = get(patient, 'sumOfServices');
  const sumOfPayments = get(patient, 'sumOfPayments');
  const hasSums = sumOfServices !== undefined && sumOfServices !== null && sumOfPayments !== undefined && sumOfPayments !== null;

  // Lists
  const servicesState = listState(services);
  const paymentsState = listState(payments);
  const receiptsState = listState(receipts);
  const servicesData = get(services, 'data', {});
  const paymentsData = get(payments, 'data', {});
  const receiptsData = get(receipts, 'data', {});

  const movements = useMemo(() => recentMovements(servicesData, paymentsData), [servicesData, paymentsData]);
  const shownMovements = showAll ? movements : movements.slice(0, RECENT_LIMIT);
  const movementsLoading = movements.length === 0 && (servicesState === 'loading' || paymentsState === 'loading');
  const movementsFailed = movements.length === 0 && !movementsLoading && (servicesState === 'error' || paymentsState === 'error');
  const nothingYet = movements.length === 0 && !movementsLoading && !movementsFailed;
  const everythingEmpty = nothingYet && receiptsState === 'ready' && size(receiptsData) === 0;

  const methodText = (payment) => paymentMethodText(paymentMethods, payment, _('overview/payment'));
  // One list failed while the other has movements: say which, or the merged list looks complete
  const partialErrors = movements.length > 0 ? ['services', 'payments'].filter((key) => (key === 'services' ? servicesState : paymentsState) === 'error') : [];

  const actions = [
    { kind: 'service', label: _('overview/new_service'), aria: _('action/new_service'), onClick: onNewService },
    { kind: 'payment', label: _('overview/new_payment'), aria: _('action/new_payment'), onClick: onNewPayment },
    { kind: 'receipt', label: _('overview/new_receipt'), aria: _('action/new_receipt'), onClick: onNewReceipt },
  ];

  const lists = [
    { key: 'services', kind: 'service', title: _('label/services'), state: servicesState, data: servicesData },
    { key: 'payments', kind: 'payment', title: _('label/payments'), state: paymentsState, data: paymentsData },
    { key: 'receipts', kind: 'receipt', title: _('label/receipts'), state: receiptsState, data: receiptsData },
  ];

  const listSummary = (list) => {
    if (list.state === 'loading') return <Line width="w40" />;
    if (list.state === 'error') return <span className="financialListRow-error">{_('overview/row_error')}</span>;
    const count = size(list.data);
    if (count === 0) return _(`overview/${list.key}_none`);
    const latest = latestDate(list.data);
    return (
      <React.Fragment>
        {t(`financial:overview/${list.key}_count`, { count })}
        {latest && ` · ${t('financial:overview/latest', { date: shortDate(latest) })}`}
      </React.Fragment>
    );
  };

  const renderMovement = ({ kind, key, item }) => {
    const isPayment = kind === 'payment';
    const isLoading = !!get(isPayment ? paymentLoadingIds : serviceLoadingIds, String(item.id));
    const counts = countsTowardBalance(kind, item.status);
    // A contracted service needs no chip (it's the usual); a payment always shows its own, as Badge words them
    const showBadge = !!item.status && (isPayment || item.status !== 'confirmed');
    const open = () => (isPayment ? onOpenPayment(item) : onOpenService(item));

    return (
      <li key={key}>
        <button
          type="button"
          className={classNames('financialMovement', `financialMovement-${kind}`, { loading: isLoading })}
          onClick={isLoading ? undefined : open}
          aria-disabled={isLoading || undefined}
          aria-busy={isLoading || undefined}
        >
          {isLoading ? <span className="financialKind"><Spinner /></span> : <Kind kind={kind} size={18} />}
          <span className="financialMovement-main">
            <span className="financialMovement-body">
              <span className="financialMovement-title">{isPayment ? methodText(item) : item.name}</span>
              {' '}
              <span className="financialMovement-meta">
                <FormattedText type="date" text={item.date} />
                {showBadge && ' '}
                {showBadge && <Badge className={item.status} inline />}
              </span>
            </span>
            {' '}
            <span className={classNames('financialMovement-value', { counted: counts, canceled: item.status === 'canceled' })}>
              {isPayment && counts && '+ '}
              <FormattedText type="currency" text={item.price} />
            </span>
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className="financialOverview">
      <section className={classNames('financialSummary', status && `is-${status}`)} aria-label={_('label/balance')}>
        <div className="financialSummary-top">
          <span className="financialSummary-label">{_('label/balance')}</span>
          {status && <span className={`financialStatus financialStatus-${status}`}>{_(`overview/status/${status}`)}</span>}
        </div>
        <div className={classNames('financialSummary-amount', { refreshing: balancePending && hasBalance })}>
          {/* Under half a cent reads "Em dia": shown as R$ 0,00, not R$ -0,00 */}
          {hasBalance && <FormattedText type="currency" text={status === 'settled' ? 0 : balance} />}
          {!hasBalance && !balanceFailed && <span className="skeleton"><Line width="w40" /></span>}
          {!hasBalance && balanceFailed && (
            <span className="financialSummary-error">
              {_('overview/balance_error')}
              <button type="button" className="financialLinkButton" onClick={onRetry}>{_('overview/retry')}</button>
            </span>
          )}
        </div>
        {(hasSums || balancePending) && (
          <dl className={classNames('financialSummary-split', { refreshing: balancePending && hasSums })}>
            <div>
              <dt>{_('overview/contracted')}</dt>
              <dd>{hasSums ? <FormattedText type="currency" text={sumOfServices} /> : <span className="skeleton"><Line width="w75" /></span>}</dd>
            </div>
            <div>
              <dt>{_('overview/paid')}</dt>
              <dd>{hasSums ? <FormattedText type="currency" text={sumOfPayments} /> : <span className="skeleton"><Line width="w75" /></span>}</dd>
            </div>
          </dl>
        )}
      </section>

      <div className="financialActions">
        {actions.map((action) => (
          <button key={action.kind} type="button" className="financialAction" onClick={() => action.onClick()} aria-label={action.aria}>
            <span className="financialAction-circle">
              <Kind kind={action.kind} size={24} />
              <span className="financialAction-plus" aria-hidden="true"><Icon iconName="add" size={12} /></span>
            </span>
            <span className="financialAction-label" aria-hidden="true">{action.label}</span>
          </button>
        ))}
      </div>

      {!everythingEmpty && (
        <ul className="financialLists">
          {lists.map((list) => (
            <li key={list.key}>
              <button type="button" className="financialListRow" onClick={() => onOpenList(list.key)}>
                <Kind kind={list.kind} size={18} />
                <span className="financialListRow-main">
                  <span className="financialListRow-body">
                    <span className="financialListRow-title">{list.title}</span>
                    {' '}
                    <span className={classNames('financialListRow-summary', { skeleton: list.state === 'loading' })}>{listSummary(list)}</span>
                  </span>
                  <Icon iconName="arrow-right" size={22} className="financialListRow-chevron" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <section className="financialRecent" aria-labelledby="financialRecentTitle">
        <div className="financialRecent-header">
          <h2 id="financialRecentTitle">{_('overview/recent')}</h2>
          {movements.length > RECENT_LIMIT && (
            <button type="button" className="financialLinkButton" onClick={() => setShowAll(!showAll)} aria-expanded={showAll}>
              {showAll ? _('action/see_less', 'global') : _('overview/see_all')}
            </button>
          )}
        </div>

        {movementsLoading && (
          <div className="financialRecent-loading skeleton" aria-busy="true" aria-label={_('label/loading', 'global')}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="financialMovement" aria-hidden="true">
                <span className="financialKind" />
                <span className="financialMovement-main">
                  <span className="financialMovement-body"><Line width="w75" /><Line width="w40" /></span>
                  <Line width="w15" />
                </span>
              </div>
            ))}
          </div>
        )}

        {movementsFailed && (
          <div className="financialRecent-error" role="alert">
            <p>{_('overview/list_error')}</p>
            <button type="button" className="financialLinkButton" onClick={onRetry}>{_('overview/retry')}</button>
          </div>
        )}

        {nothingYet && (
          <div className="financialRecent-empty">
            <Kind kind="payment" size={26} />
            <h3>{_('overview/empty/title')}</h3>
            <p>{_('overview/empty/hint')}</p>
            <button type="button" className="financialPrimaryButton" onClick={() => onNewService()}>{_('overview/empty/action')}</button>
          </div>
        )}

        {partialErrors.length > 0 && (
          <div className="financialRecent-error financialRecent-partialError" role="alert">
            <p>{partialErrors.map((key) => _(`overview/${key}_error`)).join(' ')}</p>
            <button type="button" className="financialLinkButton" onClick={onRetry}>{_('overview/retry')}</button>
          </div>
        )}

        {shownMovements.length > 0 && <ul className="financialRecent-list">{shownMovements.map(renderMovement)}</ul>}
      </section>
    </div>
  );
};

export default PatientFinancialOverview;
